import { Injectable } from '@angular/core';
import { GlobalQuery, GlobalService } from '../global';
import { HttpClient } from '@angular/common/http';
import { CreativeUnitPackageStore } from './ad-unit-package.store';
import { LocalStorageMockService } from '../../../shared/local-storage-mock.service';
import { firstValueFrom, Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DefaultResponse, FindResponse } from '../session';
import { catchError, map } from 'rxjs/operators';
import { QueryParams } from '../collection/collection.model';
import { getPluginByName, GsapPluginSelection } from './ad-animations/gsap-plugins.config';
import {
	CreativeUnitPackageFindDto,
	CreativeUnitPackageSummaryFindDto
} from '../../../../../../api/src/creative-unit-package/models/creative-unit-package-find.dto';
import { CustomizationUtils } from '../../../../../../api/src/creative-unit-package/utils/customization.utils';
import {
	CustomizationType,
	getCustomizationClassByType
} from '../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { CreativeUnitPackageDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import {
	CreativeUnitPackage,
	PublicCreativeUnitPackageSummary,
	SortStrategy,
	SortString,
	SummarySortString
} from '../../../../../../api/src/creative-unit-package/creative-unit-package.entity';
import { CreativeUnitPackageStatusDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit-package-status.dto';
import { isString, cloneDeep, merge } from 'lodash';
import { AssetTemplateSelectElement } from '../../../shared/components/asset-template-selector/asset-template-selector.model';
import { AssetDto } from '../../../../../../api/src/asset/models/asset.dto';
import { CreativeUnitPackageQuery } from './ad-unit-package.query';
import { AssetService } from '../asset/asset.service';
import { sortArray } from '../../utils/array.utils';
import { SortDirection } from '../../../../../../api/src/_core/utils/utils.paging';
import { CreativePackageUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-package.utils';
import { CreativeUnitListingDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit-listing.dto';
import {
	CreativeUnitDto,
	CreativeUnitWithCustomizationsDto
} from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';
import { LayerDto } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { MergePropertiesUtils } from '../../../../../../api/src/creative-unit-package/utils/merge-properties.utils';
import { CreativeUnitUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-unit.utils';
import { AdStudioContainerService } from './ad-container.service';
import { AdStudioContainerStore } from './ad-container.store';
import { ChangeHistoryEvent } from './ad-unit.model';
import { CartItemGroup } from '../cart/cart.model';
import { CartJobDto } from '../../../../../../api/src/encode/models/job-def.dto';
import { PublicEncodeJob } from '../../../../../../api/src/encode/encode-job.entity';
import { arrayUpsert } from '@datorama/akita';
import { LayersUtils } from '../../../../../../api/src/creative-unit-package/utils/layers.utils';

@Injectable({ providedIn: 'root' })
export class CreativeUnitPackageService {
	public mock = new LocalStorageMockService('adPackages');

	constructor(
		private globalQuery: GlobalQuery,
		private globalService: GlobalService,
		private creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private creativeUnitPackageStore: CreativeUnitPackageStore,
		private adStudioContainerStore: AdStudioContainerStore,
		private adStudioContainerService: AdStudioContainerService,
		private assetService: AssetService,
		private http: HttpClient
	) {}

	/**
	 * Fetch an array of Creative Unit Packages
	 */
	public get({ includeVersions, onlyPublished, filterId, originalVersionId}: QueryParams) {
		const params = {
			includeVersions: includeVersions,
			onlyPublished: onlyPublished
		}

		if (filterId) {
			params['id'] = filterId;
		}
		if (originalVersionId) {
			params['originalVersionId'] = originalVersionId;
		}

		return this.http.get<DefaultResponse<CreativeUnitPackageDto[]>>(`${environment.apiUrl}/creative-unit-packages`, {
			params
		});
	}

	/**
	 * Fetch a result of Creative Unit Packages based on filter / pagination params.
	 */
	public find({ search, page, limit, sortBy, includeVersions }: QueryParams) {
		const findReq: CreativeUnitPackageFindDto = {
			name: search || undefined,
			sort: sortBy ? (sortBy.direction === 'ASC' ? SortStrategy.ASC : SortStrategy.DESC) : undefined,
			sortBy: sortBy ? (sortBy.id as SortString) : undefined,
			includeVersions: includeVersions
		};

		return this.http
			.post<FindResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages/find`, findReq, {
				params: { page: page || 1, perPage: limit || 10 }
			})
			.pipe(
				tap(response => {
					this.creativeUnitPackageStore.set(response.results);
				})
			);
	}

	/**
	 * Fetch a result of Creative Unit Package summaries based on filter / pagination params.
	 */
	public findSummaries({ search, page, limit, sortBy, onlyPublished, businessIds, include, fromReporting}: QueryParams & { include?: string[] }) {
		const findReq: CreativeUnitPackageSummaryFindDto = {
			name: search || undefined,
			sort: sortBy ? (sortBy.direction === 'ASC' ? SortStrategy.ASC : SortStrategy.DESC) : undefined,
			sortBy: sortBy ? (sortBy.id as SummarySortString) : undefined,
			onlyPublished: onlyPublished ? true : false,
			businessIds: businessIds || undefined,
			include: include || undefined,
			fromReporting: fromReporting || undefined
		};

		return this.http.post<FindResponse<PublicCreativeUnitPackageSummary>>(`${environment.apiUrl}/creative-unit-packages/find/summary`, findReq, {
			params: { page: page || 1, perPage: limit || 25 }
		});
	}

	/**
	 * Fetch a Creative Unit Package by ID.
	 */
	public findOne(id: string, includeDeleted: boolean = false, byOriginalId: boolean = false) {
		let params = {
			includeDeleted: includeDeleted,
			byOriginalId: byOriginalId
		};

		return this.http
			.get<DefaultResponse<CreativeUnitPackageDto>>(
				`${environment.apiUrl}/creative-unit-packages/${id}`,{params}
			)
			.pipe(
				map(response => response.data),
				tap(creativePackage => {
					this.creativeUnitPackageStore.upsert(creativePackage.id, creativePackage);
				})
			);
	}

	/**
	 * Sets the list of Creative Unit Packages in the Akita Store.
	 */
	public set(packages: CreativeUnitPackageDto[]) {
		this.creativeUnitPackageStore.set(packages);
	}

	/**
	 * Reset the Creative Unit Package store.
	 */
	public reset() {
		this.creativeUnitPackageStore.setActive(null);
		this.creativeUnitPackageStore.set([]);
		this.creativeUnitPackageStore.reset();
	}

	/**
	 * Adds a Creative Unit Package to the server.
	 */
	public add(pkg: CreativeUnitPackageDto) {
		return this.http.post<DefaultResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages`, pkg).pipe(
			map(response => response?.data),
			tap(creativePackage => {
				this.creativeUnitPackageStore.add(creativePackage);
			})
		);
	}

	/**
	 * Upserts a Creative Unit Package into the Akita store.
	 */
	public upsert(pkg: CreativeUnitPackageDto, silent = true) {
		if (silent) {
			this.creativeUnitPackageStore.upsert(pkg.id, pkg);
		} else {
			throw new Error('Upsert to API is not available yet.');
		}
	}

	/**
	 * Sets a Creative Unit Package as active in the Akita store.
	 */
	public setActive(id: string) {
		this.creativeUnitPackageStore.setActive(id);
	}

	/**
	 * Clears the active states for all Creative Unit Packages in the Akita store.
	 */
	public clearActive() {
		this.creativeUnitPackageStore.setActive(null);
		this.creativeUnitPackageStore.setVersionList(null);
	}

	/**
	 * Sets the active Creative Unit state for the editor.
	 */
	public setActiveCreativeUnit(id: string) {
		const previouslyActiveId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();

		if (previouslyActiveId !== id) {
			console.log('[UNIT_SWITCH] Active creative unit changed from', previouslyActiveId, 'to', id);
		}

		this.creativeUnitPackageStore.update({ editor: { activeCreativeUnitId: id } });
	}

	/**
	 * Clears the active Creative Unit state for the editor.
	 */
	public clearActiveCreativeUnit() {
		this.creativeUnitPackageStore.update({ editor: { activeCreativeUnitId: null } });
	}

	/**
	 * Updates the properties of a Creative Unit Package.  Can optionally pass those updates
	 * to the server.
	 */
	public update(pkg: Partial<CreativeUnitPackageDto>, saveToServer: boolean = false, isVersion: boolean = false, skipSanitization: boolean = false) {
		console.log('CreativeUnitPackageService: update', pkg);

		// Remove customizations from all creative units.
		// DEPRECATED: Customizations are not on the package level creative units.
		// let creativeUnits = pkg.creativeUnits.map(unit => {
		// 	let { customizations, ...rest } = unit;
		// 	return {
		// 		...rest,
		// 		animationConfig: pkg.animationConfig,
		// 		theme: pkg.theme
		// 	};
		// });

		//console.log('creativeUnits', creativeUnits);

		// Migration: Remove 'style' property from package.
		pkg = {
			...pkg,
			// @ts-ignore
			style: undefined
		};

		if (saveToServer) {
			return this.http
				.patch<DefaultResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages/${pkg.id}`, {
					...pkg,
					isVersion: undefined,
					versions: undefined,
					createdByUser: undefined,
					// Strip values from customizations, then get the default value for each customization.
					customizations: CustomizationUtils.mapAllCustomizations(pkg.customizations, customization => {
						const customizationClass = getCustomizationClassByType(customization.type);
						console.log('Setting customization values to undefined');
						return {
							...customization,
							value: undefined //|| customizationClass?.getDefaultValue({ ...customization, value: undefined }, pkg.layers) || undefined
						};
					})
				})
				.pipe(
					tap(response => {
						this.creativeUnitPackageStore.update(pkg.id, pkg);
					})
				);
		} else {
			// Skip sanitization during grouping operations to preserve layer styles
			let toUpdate = skipSanitization ? pkg : CreativePackageUtils.sanitizeCreativePackage(pkg);
			return of(
				this.creativeUnitPackageStore.update(pkg.id, {
					...toUpdate,
					creativeUnits: CreativePackageUtils.remapCreativeUnitsForSave(toUpdate.creativeUnits || [])
				})
			);
		}
	}

	public updateStatus(id: CreativeUnitPackageDto['id'], status: CreativeUnitPackageStatusDto['status'], saveToServer: boolean = false) {
		if (saveToServer) {
			return this.http
				.patch<DefaultResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages/${id}/status`, { status })
				.pipe(
					tap(response => {
						if (response.status === 'succeeded') {
							// update only status and publishedAt to keep other properties intact
							this.creativeUnitPackageStore.update(id, {
								status: response.data.status,
								publishedAt: response.data.publishedAt
							});
						}
					})
				);
		} else {
			return of(this.creativeUnitPackageStore.update(id, { status }));
		}
	}

	/**
	 * Ensures a GSAP plugin is enabled in the package's animation configuration.
	 * If the plugin exists but is disabled, it will be enabled.
	 * If the plugin doesn't exist, it will be added with default settings from the centralized config.
	 * @param packageId The ID of the package to update
	 * @param pluginName The name of the plugin to enable (e.g., 'SplitTextPlugin' or 'SplitText')
	 */
	public ensurePluginEnabled(packageId: string, pluginName: string): void {
		const pkg = this.creativeUnitPackageQuery.getEntity(packageId);
		if (!pkg) {
			console.error(`Package ${packageId} not found`);
			return;
		}

		// Get or create animationConfig
		const animationConfig = pkg.animationConfig || { markers: [], gsapPlugins: [] };
		const gsapPlugins = animationConfig.gsapPlugins || [];

		// Normalize plugin name (remove 'Plugin' suffix if present)
		const normalizedPluginName = pluginName.replace('Plugin', '');

		console.log('[ensurePluginEnabled]', {
			pluginName,
			normalizedPluginName,
			existingPlugins: gsapPlugins.map(p => p.name)
		});

		// Get plugin config from centralized config
		const pluginConfig = getPluginByName(normalizedPluginName);
		if (!pluginConfig) {
			console.warn(`Plugin ${pluginName} not found in GSAP_PLUGINS config`);
			return;
		}

		// Check if plugin already exists
		const existingPluginIndex = gsapPlugins.findIndex(p =>
			p.name === pluginName ||
			p.name === normalizedPluginName ||
			p.name + 'Plugin' === pluginName
		);

		let updatedPlugins: GsapPluginSelection[];

		if (existingPluginIndex !== -1) {
			// Plugin exists, ensure it's enabled
			const existingPlugin = gsapPlugins[existingPluginIndex];
			console.log('[ensurePluginEnabled] Plugin exists', { existingPlugin });
			if (!existingPlugin.enabled) {
				console.log('[ensurePluginEnabled] Enabling plugin');
				// Create new array with updated plugin (avoid mutation)
				updatedPlugins = gsapPlugins.map((plugin, index) =>
					index === existingPluginIndex
						? { ...plugin, enabled: true }
						: plugin
				);

				// Update the package with full package object
				this.update({
					...pkg,
					animationConfig: {
						...animationConfig,
						gsapPlugins: updatedPlugins
					}
				}, false);
			} else {
				console.log('[ensurePluginEnabled] Plugin already enabled');
			}
		} else {
			console.log('[ensurePluginEnabled] Adding new plugin');
			// Plugin doesn't exist, create new array with added plugin (avoid mutation)
			updatedPlugins = [
				...gsapPlugins,
				{
					name: normalizedPluginName,
					enabled: true,
					version: '3.13.0',
					fileSize: pluginConfig.fileSize,
					cdnUrl: pluginConfig.cdnUrl
				}
			];

			console.log('[ensurePluginEnabled] Updated plugins', updatedPlugins);

			// Update the package with full package object
			this.update({
				...pkg,
				animationConfig: {
					...animationConfig,
					gsapPlugins: updatedPlugins
				}
			}, false);
		}
	}

	/**
	 * Removes a Creative Unit Package.
	 */
	remove(pkg: CreativeUnitPackageDto | PublicCreativeUnitPackageSummary) {
		return this.http.delete<DefaultResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages/${pkg.id}`).pipe(
			tap(response => {
				this.creativeUnitPackageStore.remove(pkg.id);
			})
		);
	}

	/**
	 * sync customizations with dynamic link
	 */
	syncCustomizations(pkg: CreativeUnitPackageDto) {
		return this.http.put<DefaultResponse<any>>(`${environment.apiUrl}/creative-unit-packages/${pkg.id}/sync-customizations`, {});
	}

	async convertSasstoCss(styles: string): Promise<string> {
		if (isString(styles)) {
			try {
				// @ts-ignore Import module
				const sass = await import(/* webpackIgnore: true */ 'sass');
				return new Promise((resolve, reject) => {
					resolve(sass.compileString(styles)?.css);
				});
			} catch (error) {
				console.log('Error compiling styles', error);
			}
		}
	}

	async validateSass(styles: string): Promise<any> {
		if (isString(styles)) {
			try {
				// @ts-ignore Import module
				const sass = await import(/* webpackIgnore: true */ 'sass');
				return new Promise((resolve, reject) => {
					try {
						resolve(sass.compileString(styles));
					} catch (e) {
						reject(e);
					}
				});
			} catch (error) {
				console.log('Error compiling styles', error);
			}
		}
	}

	duplicate(id: CreativeUnitPackageDto['id']) {
		return this.http
			.post<DefaultResponse<CreativeUnitPackageDto>>(`${environment.apiUrl}/creative-unit-packages/${id}/duplicate`, {})
			.pipe(
				catchError(err => {
					this.globalService.triggerErrorMessage(
						undefined,
						$localize`:Error Message@@adEditorDuplicateTemplateErrorMessageText:Error duplicating template`
					);
					throw err;
				}),
				map(response => response?.data),
				tap(creativePackage => {
					this.creativeUnitPackageStore.add(creativePackage);
				})
			);
	}

	selectSelectorElements(): Observable<AssetTemplateSelectElement[]> {
		return this.creativeUnitPackageQuery.selectAll().pipe(
			map(packages =>
				packages.map(pkg => {
					return {
						id: pkg.id,
						name: pkg.name,
						description: pkg.description,
						status: pkg.status,
						tags: pkg.tags,
						// isFeatured: pkg.featured,
						// isRecent: pkg.recent,
						fileType: this.assetService.getMediaTypeFromUrl(pkg.thumbnail?.assetPath),
						categories: pkg.categories as any,
						thumbnail: pkg.thumbnail as AssetDto,
						previewUrl: pkg.thumbnail?.assetPath,
						modified: pkg.modified,
						isPinned: pkg.isPinned || false, // Now available from API
						originalVersionId: pkg.originalVersionId

					} as AssetTemplateSelectElement;
				})
			),
			map(packages => sortArray(packages, 'modified', SortDirection.ASC))
		);
	}

	/**
	 * Get templates with pin status included
	 */
	selectSelectorElementsWithPinStatus(): Observable<AssetTemplateSelectElement[]> {
		return this.selectSelectorElements().pipe(
			map(templates => {
				// For now, return templates as-is. Pin status will be updated by components.
				// This method is a placeholder for future enhancement where we could
				// combine the templates and pinned status in a single observable.
				return templates;
			})
		);
	}

	/**
	 * Pin a template for the current user
	 */
	public pinTemplate(templateId: string): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}/creative-unit-packages/pinned/${templateId}`, {});
	}

	/**
	 * Unpin a template for the current user
	 */
	public unpinTemplate(templateId: string): Observable<any> {
		return this.http.delete<any>(`${environment.apiUrl}/creative-unit-packages/pinned/${templateId}`);
	}

	/**
	 * Get all pinned templates for the current user
	 */
	public getPinnedTemplates(): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/creative-unit-packages/pinned`);
	}

	/**
	 * Check if a template is pinned
	 */
	public isPinned(templateId: string): Observable<{ isPinned: boolean }> {
		return this.http.get<any>(`${environment.apiUrl}/creative-unit-packages/pinned/${templateId}/status`).pipe(
			map(response => response.data)
		);
	}

	/**
	 * Reorder pinned templates
	 */
	public reorderPins(pins: { templateId: string; sortOrder: number }[]): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}/creative-unit-packages/pinned/reorder`, { pins });
	}

	private sanitizeImportPackageData(activePackage: CreativeUnitPackageDto) {
		return {
			...activePackage,
			isVersion: undefined,
			createdByUserId: undefined,
			originalVersionId: undefined,
			versionComment: undefined,
			status: undefined,
			publishedAt: undefined,
		};
	}

	public createPackageFromJson(packageData: CreativeUnitPackageDto) {
		return this.http.post(`${environment.apiUrl}/creative-unit-packages/create-package-from-json`, packageData);
	}

	importCreativePackage(currentPackageId: string, activePackage: CreativeUnitPackageDto) {
		const pkg = this.sanitizeImportPackageData(activePackage);
		return this.http.post(`${environment.apiUrl}/creative-unit-packages/${currentPackageId}/import-package-json`, pkg);
	}

	importCreativePackageNewVersion(currentPackageId: string, activePackage: CreativeUnitPackageDto) {
		const pkg = this.sanitizeImportPackageData(activePackage);
		return this.http.post(`${environment.apiUrl}/creative-unit-packages/${currentPackageId}/import-package-json-new`, pkg);
	}

	/**
	 * Export Creative Unit Package by ID.
	 */
	public exportCreativePackage(id: string) {
		return this.http.get(`${environment.apiUrl}/creative-unit-packages/${id}/export-package`, {
			responseType: 'blob',
			observe: 'response'
		});
	}

	/**
	 * Fetch list of versions for template version
	 */
	public async getVersionList(unitId: string): Promise<void> {
		try {
		  const res = await firstValueFrom(
			this.http.get<DefaultResponse<CreativeUnitPackageDto[]>>(
			  `${environment.apiUrl}/creative-unit-packages/${unitId}/versions/list`
			)
		  );
		  this.creativeUnitPackageStore.setVersionList(res.data);
		} catch (error) {
			console.log('Error loading version list', error);
		}
	}

	public createNewVersion(id: string, pkg: CreativeUnitPackageDto) {
		return this.http.post(`${environment.apiUrl}/creative-unit-packages/${id}/versions/new`, pkg);
	}

	updateVersionListItem(updatedVersion: CreativeUnitPackageDto): void {
		this.creativeUnitPackageStore.updateVersionListItem(updatedVersion);
	}

	/**
	 * Pushes a change history event to the store.
	 * @param event The event to push.
	 */
	pushHistoryEvent(event: ChangeHistoryEvent) {
		this.adStudioContainerStore.update(state => {
			const newHistory = {
				...state.changeHistoryState,
				undoStack: [...state.changeHistoryState.undoStack, event],
				redoStack: [],
				undoStackSize: state.changeHistoryState.undoStack.length + 1,
				redoStackSize: 0
			};
			if (newHistory.undoStack.length > newHistory.undoStackLimit) {
				newHistory.undoStack.shift();
			}
			return {
				changeHistoryState: newHistory
			};
		});
	}

	/**
	 * Undoes the last event in the history.
	 */
	undoHistoryEvent(currentState: ChangeHistoryEvent) {
		this.adStudioContainerStore.update(state => {
			if (state.changeHistoryState.undoStack.length === 0) {
				console.warn('Undo stack is empty. Cannot undo history event.');
				return state; // Return the current state unchanged
			}
			const event = state.changeHistoryState.undoStack[state.changeHistoryState.undoStack.length - 1];

			console.log('Undoing event', event, state.changeHistoryState);
			const newHistory = {
				...state.changeHistoryState,
				undoStack: state.changeHistoryState.undoStack.slice(0, -1),
				redoStack: [...state.changeHistoryState.redoStack, currentState],
				undoStackSize: state.changeHistoryState.undoStack.length - 1,
				redoStackSize: state.changeHistoryState.redoStack.length + 1
			};
			if (newHistory.redoStack.length > newHistory.redoStackLimit) {
				newHistory.redoStack.shift();
			}

			this.update(event.changes);

			return {
				changeHistoryState: newHistory
			};
		});
	}

	/**
	 * Redoes the last undone event in the history.
	 */
	redoHistoryEvent(currentState: ChangeHistoryEvent) {
		this.adStudioContainerStore.update(state => {
			const event = state.changeHistoryState.redoStack[state.changeHistoryState.redoStack.length - 1];
			console.log('Redoing event', event, state.changeHistoryState);
			const newHistory = {
				...state.changeHistoryState,
				undoStack: [...state.changeHistoryState.undoStack, currentState],
				redoStack: state.changeHistoryState.redoStack.slice(0, -1),
				undoStackSize: state.changeHistoryState.undoStack.length + 1,
				redoStackSize: state.changeHistoryState.redoStack.length - 1
			};
			if (newHistory.undoStack.length > newHistory.undoStackLimit) {
				newHistory.undoStack.shift();
			}
			if (newHistory.redoStack.length > newHistory.redoStackLimit) {
				newHistory.redoStack.shift();
			}

			this.update(event.changes);

			return {
				changeHistoryState: newHistory
			};
		});
	}

	/**
	 * Resets the change history state.
	 */
	clearHistory() {
		this.adStudioContainerStore.update(state => ({
			changeHistoryState: {
				...state.changeHistoryState,
				undoStack: [],
				redoStack: [],
				undoStackSize: 0,
				redoStackSize: 0
			}
		}));
	}

	public async createCreativeUnitFromListing(
		listing: CreativeUnitListingDto,
		pkg: CreativeUnitPackageDto,
		creativeUnitId?: string,
		mergeTagState?: any,
		noOverrides?: boolean,
		activeGroup?: CartItemGroup
	): Promise<CreativeUnitWithCustomizationsDto> {
		console.log('Creating from listing', listing, creativeUnitId, pkg);
		let unit = {
			id: new Date().getTime().toString() + Math.random().toString(36).substring(2, 9),
			name: listing.name,
			packageId: pkg?.id,
			listingId: listing.id,
			metadata: listing.metadata,
			dimensions: {
				width: listing.dimensions?.width,
				height: listing.dimensions?.height
			},
			templateCreativeUnit: listing?.name || null,
			layers: [],
			locale: {}
		};

		// If the listing is a group, add a group id.
		if (listing.creativeUnitIds?.length > 1 || listing.children?.length) {
			unit['group'] = {
				id: listing.id,
				name: listing.name,
				instanceId: listing.instanceId
			};
		}

		// Find the creative package unit that matches the size.
		let findId = creativeUnitId || listing.creativeUnitIds?.[0];
		let size: CreativeUnitDto | CreativeUnitListingDto = pkg.creativeUnits.find(unit => unit.id === findId);

		// If we didn't find a creative unit, check if findId corresponds to a listing
		if (!size && findId) {
			const foundListing = pkg.creativeUnitListingSettings?.listings?.find(l => l.id === findId);
			if (foundListing && foundListing.creativeUnitIds?.length > 0) {
				// Use the first creative unit from the found listing
				const fallbackCreativeUnitId = foundListing.creativeUnitIds[0];
				size = pkg.creativeUnits.find(unit => unit.id === fallbackCreativeUnitId);
				console.log('Found listing instead of creative unit, using fallback:', foundListing.name, 'with creative unit:', size?.name);
			}
		}

		// Final fallback to the original listing if still no size found
		if (!size) {
			size = listing;
		}

		// Merge the size locale with the listing locale.
		if (size.locale) {
			unit.locale = {
				...listing.locale,
				...size.locale
			};
		}

		// Fill in our dimensions if they didn't exist on the listing.  This will happen with group listings.
		if (!unit.dimensions?.width || !unit.dimensions?.height) {
			if (size?.dimensions) {
				unit.dimensions = { ...size.dimensions };
			} else {
				// Fallback to basic dimensions
				unit.dimensions = {
					width: 300,
					height: 250
				};
			}
		}

		// If the listing is a group, Combine the names together.  Include the locale if it exists.
		if (listing.creativeUnitIds?.length > 1) {
			unit.name = `${size.name}`;

			// Iterate through the name locale and translate in relevant languages.
			if (typeof listing.locale?.name === 'object') {
				let newLocale = {
					...unit.locale,
					name: {}
				};

				for (let [key, value] of Object.entries(listing.locale.name)) {
					// newLocale.name[key] = `${value} (${size.locale?.name?.[key] || size.name})`;
					newLocale.name[key] = `${size.locale?.name?.[key] || size.name}`;
				}

				unit.locale = newLocale;
			}
		}

		// If we're creating from the package, find a matching unit and copy its properties.
		// This is mostly used for the end user experience.
		let createdUnit: CreativeUnitWithCustomizationsDto;
		let unitFromPackage: CreativeUnitDto;

		// First, check if size is a CreativeUnitDto and use its id
		if ('packageId' in size && 'layers' in size) {
			// size is a CreativeUnitDto
			for (let creativeUnit of pkg.creativeUnits) {
				if (creativeUnit.id === size.id) {
					unitFromPackage = creativeUnit;
					break;
				}
			}
		} else {
			// size is a CreativeUnitListingDto, try to find by creativeUnitIds
			const sizeAsListing = size as CreativeUnitListingDto;
			if (sizeAsListing.creativeUnitIds?.length > 0) {
				for (let creativeUnit of pkg.creativeUnits) {
					if (creativeUnit.id === sizeAsListing.creativeUnitIds[0]) {
						unitFromPackage = creativeUnit;
						break;
					}
				}
			}
		}

		if (!unitFromPackage) {
			// Ensure dimensions exist before calling getMostSimilarCreativeUnit
			if (unit.dimensions?.width && unit.dimensions?.height) {
				unitFromPackage = CreativeUnitUtils.getMostSimilarCreativeUnit(
					pkg.creativeUnits || [],
					String(unit.dimensions.width),
					String(unit.dimensions.height)
				);
			}
		}

		// console.log('Creating from package', unitFromPackage, pkg);

		let overrides: any = {
			...unit,
			creativeUnitId: unitFromPackage?.id,
			dimensions: {
				...unit.dimensions,
				aspectMin: unitFromPackage?.dimensions?.aspectMin,
				aspectMax: unitFromPackage?.dimensions?.aspectMax,
				ppi: unitFromPackage?.dimensions?.ppi,
				defaultDisplayUnit: unitFromPackage?.dimensions?.defaultDisplayUnit || 'px',
				canTransformInto: unitFromPackage?.dimensions?.canTransformInto
			},
			containerClasses: CreativeUnitUtils.getContainerClasses(
				unitFromPackage?.containerClasses,
				pkg.containerClasses,
				unitFromPackage?.appendContainerClasses
			),
			variableOverrides: unitFromPackage?.variableOverrides,
			exportTypes: listing?.exportTypes || unitFromPackage?.exportTypes,
			exportCropBox: unitFromPackage?.exportCropBox,
			scripts: unitFromPackage?.scripts || [],
		};

		// console.log('overrides', overrides);

		// Add in animation config if it exists on the package, unit, or listing.
		if (pkg?.animationConfig || unitFromPackage?.animationConfig || listing.animationConfig) {
			const packageConfig = cloneDeep(pkg?.animationConfig || {});
			const unitConfig = cloneDeep(unitFromPackage?.animationConfig || {});
			const listingConfig = cloneDeep(listing.animationConfig || {});

			// Merge the configs: package < unit < listing (listing has highest priority)
			overrides.animationConfig = merge(packageConfig, unitConfig, listingConfig);

			// For gsapPlugins, we want to merge the arrays intelligently
			if (packageConfig.gsapPlugins || unitConfig.gsapPlugins || listingConfig.gsapPlugins) {
				const pluginMap = new Map();

				// Add package plugins first
				if (packageConfig.gsapPlugins) {
					packageConfig.gsapPlugins.forEach(plugin => {
						pluginMap.set(plugin.name, plugin);
					});
				}

				// Override with unit plugins (if they exist)
				if (unitConfig.gsapPlugins) {
					unitConfig.gsapPlugins.forEach(plugin => {
						pluginMap.set(plugin.name, plugin);
					});
				}

				// Override with listing plugins (highest priority)
				if (listingConfig.gsapPlugins) {
					listingConfig.gsapPlugins.forEach(plugin => {
						pluginMap.set(plugin.name, plugin);
					});
				}

				// Convert back to array
				overrides.animationConfig.gsapPlugins = Array.from(pluginMap.values());

			}
		}

		// Add in print config if it exists on the package or unit.
		if (pkg?.printConfig || unitFromPackage?.printConfig) {
			overrides.printConfig = merge(cloneDeep(pkg?.printConfig || {}), cloneDeep(unitFromPackage?.printConfig || {}));
		}

		// Add in html config if it exists on the package or unit.
		if (pkg?.htmlConfig || unitFromPackage?.htmlConfig) {
			overrides.htmlConfig = merge(cloneDeep(pkg?.htmlConfig || {}), cloneDeep(unitFromPackage?.htmlConfig || {}));
		}

		// Add in interactivity if it exists on the package or unit.
		if (pkg?.interactivity || unitFromPackage?.interactivity) {
			overrides.interactivity = merge(cloneDeep(pkg?.interactivity || {}), cloneDeep(unitFromPackage?.interactivity || {}));
		}

		// Add in metadata if it exists on the package or unit.
		if (listing?.metadata || unitFromPackage?.metadata) {
			overrides.metadata = merge(cloneDeep(listing?.metadata || {}), cloneDeep(unitFromPackage?.metadata || {}));
		}

		// Merge Linked Layers with unit layers.
		let layers: LayerDto[] = CreativeUnitUtils.mergePackageLayersWithUnitLayers(pkg?.layers || [], unitFromPackage?.layers || []);

		// If we have the noOverrides flag, we're likely adding a new unit in the template creator.
		// This means we don't want to automatically add a bunch of overrides, we want to default to the package config
		// and then let the user add in their own overrides.
		if (noOverrides) {
			console.log('No overrides', unitFromPackage);

			// We don't need anything from layers except the styles to preserve some responsiveness in positioning.
			layers = LayersUtils.mapAllLayers(layers, layer => {
				return {
					id: layer.id,
					type: layer.type,
					label: layer.label,
					style: layer.style,
					layers: layer.layers
				};
			});

			// Don't bring styles, variables, or themes in.
			createdUnit = CreativeUnitUtils.createCreativeUnit(
				overrides,
				layers,
				[]
			);
		} else {
			createdUnit = CreativeUnitUtils.createCreativeUnit(
				overrides,
				layers,
				[],
				unitFromPackage?.styles,
				pkg?.variables,
				pkg?.theme
			);
		}


		console.log('Created Unit with Mappings', createdUnit);

		// Apply default values to customizations.
		let customizations = unitFromPackage?.['customizations'] || pkg?.customizations || [];

		// If we had mapped customizations from the listing, merge the values in here.
		if (listing.mappings?.customizations?.length > 0) {
			// console.log('Merging mapped customizations', mappedCustomizations);
			customizations = CustomizationUtils.mapAllCustomizations(customizations, customization => {
				const mapped = listing.mappings.customizations.find(c => c.id === customization.id);
				if (mapped) {
					// console.log('Merging customization', customization.label, mapped);
					return {
						...customization,
						dirty: mapped.dirty,
						value: mapped['value']
					};
				}
				return customization;
			});

			// console.log('After merge', customizations);
		}

		// If the listing had mappings, apply them to the new unit.
		if (listing.mappings) {
			let state = MergePropertiesUtils.getMergeableProperties(pkg, undefined, createdUnit, activeGroup);
			// console.log('Applying Mappings', listing.mappings);
			createdUnit = CreativeUnitUtils.applyMappingsToCreativeUnit({
				...createdUnit,
				customizations: customizations
			}, listing.mappings, state, pkg, false);

			// Check if mappings has a specific mapping for this unit.
			let creativeUnitMapping = listing.mappings.creativeUnits?.find(unit => unit.id === findId);
			if (creativeUnitMapping) {
				// console.log('Applying Unit Mappings', creativeUnitMapping);
				createdUnit = CreativeUnitUtils.applyMappingsToCreativeUnit({
					...createdUnit,
					customizations: customizations
				}, creativeUnitMapping, state, pkg, true);
			}
		}

		// console.log('About to apply defaults', customizations, createdUnit.layers);
		createdUnit.customizations = await this.adStudioContainerService.applyDynamicDefaultValues(
			createdUnit.customizations,
			pkg,
			mergeTagState?.business,
			createdUnit,
			undefined,
			activeGroup
		);

		// Remove any manual croppings from the customizations.
		createdUnit.customizations = CustomizationUtils.mapAllCustomizations(createdUnit.customizations, customization => {
			if (customization.type === CustomizationType.IMAGE_PICKER && customization.value?.target) {
				// console.log('Removing Cropping', customization);

				customization = {
					...customization,
					value: {
						...customization.value,
						target: undefined
					}
				}

			}

			return customization;
		});

		// console.log('Final created unit', createdUnit);

		return createdUnit;
	}

	public submitPreviewJob(packageId: CreativeUnitPackage['id'], request: CartJobDto) {
		return this.http.post<DefaultResponse<PublicEncodeJob>>(`${environment.apiUrl}/creative-unit-packages/${packageId}/submit-preview-job`, request);
	}


	public addEncodeJobToPackage(job: PublicEncodeJob, pkg: CreativeUnitPackageDto) {
		this.creativeUnitPackageStore.update(pkg.id, {
			encodeJobs: arrayUpsert(pkg.encodeJobs || [], job.id, job)
		});
	}
}
